import { createSlice, createAsyncThunk, PayloadAction } from '@reduxjs/toolkit';
import { studentsService } from '../../services/studentsService';
import { RootState } from '../index';
import { selectAuthToken } from '../selectors/authSelectors';
import { isMobileClassroomMode } from '../../utils/mobileClassroomUtils';

// Simplified Student interface with only UI-needed fields
export interface Student {
  id: string;
  name: string;
  class: string;
  school: string;
  photo: string;
  phoneNumber: string;
  grade: string; // Added for API calls
  subscribedSubjects?: string[]; // Array of subjects the student has subscribed to
}

interface StudentState {
  students: Student[];
  selectedStudent: Student | null;
  studentsCount: number;
  loading: boolean;
  error: string | null;
  isMobileClassroom: boolean;
}

// Update initial state
const initialState: StudentState = {
  students: [],
  selectedStudent: null,
  studentsCount: 0,
  loading: false,
  error: null,
  isMobileClassroom: isMobileClassroomMode(),
};

// Update async thunk
export const fetchStudents = createAsyncThunk(
  'students/fetchStudents',
  async (parentId: string, { getState }) => {
    const state = getState() as RootState;
    const token = selectAuthToken(state);
    
    if (!token) {
      throw new Error('No authentication token available');
    }
    
    const response = await studentsService.getStudents(parentId, token);
    
    // Map API response to simplified Student interface
    const mappedStudents = response.data.map((student: any) => {

      return {
        id: student._id || student.id || `student-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
        name: student.fullName || 'Unknown Student',
        class: (student.grade || 'Unknown') + (student.section ? ` ${student.section}` : ''),
        school: student.schoolName || 'Unknown School',
        photo: student.profileImageurl || '',
        phoneNumber: student.phoneNumber || '',
        grade: student.grade || 'Class-01', // Add grade for API calls with fallback
        subscribedSubjects: student.subjects?.length > 0 ? student.subjects : [], // Use API subjects only, no fallback to all
      };
    });
    
    return mappedStudents;
  }
);

const studentSlice = createSlice({
  name: 'students',
  initialState,
  reducers: {
    selectStudent: (state, action: PayloadAction<Student>) => {
      state.selectedStudent = action.payload;
      // Auto-detect mobile classroom mode based on selected student's school
      state.isMobileClassroom = isMobileClassroomMode(undefined, action.payload.school);
    },
    clearSelectedStudent: (state) => {
      state.selectedStudent = null;
    },
    setStudentsCount: (state, action: PayloadAction<number>) => {
      state.studentsCount = action.payload;
    },
    clearStudents: (state) => {
      state.students = [];
      state.selectedStudent = null;
      state.studentsCount = 0;
    },
    setMobileClassroomMode: (state, action: PayloadAction<boolean>) => {
      state.isMobileClassroom = action.payload;
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchStudents.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchStudents.fulfilled, (state, action) => {
        state.loading = false;
        state.students = action.payload;
        state.studentsCount = action.payload.length;
      })
      .addCase(fetchStudents.rejected, (state, action) => {
        state.loading = false;
        state.error = action.error.message || 'Failed to fetch students';
      });
  },
});

export const { selectStudent, clearSelectedStudent, setStudentsCount, clearStudents, setMobileClassroomMode } = studentSlice.actions;
export default studentSlice.reducer;